"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { articlesApi } from "@/lib/api/services/articles.api";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SkeletonArticle } from "@/components/ui/skeleton";
import { FileText, Clock, ArrowRight, BookOpen, AlertCircle } from "lucide-react";

interface Article {
  id: string;
  title: string;
  author: string;
  readingTime?: string;
  category: string;
  createdAt?: string;
  excerpt?: string;
}

export default function ArticlesPage() {
  const { data: articles, isLoading, error } = useQuery({
    queryKey: ["articles-list"],
    queryFn: async () => {
      const data = await articlesApi.list({ page: 1, perPage: 12, sort: "createdAt", order: "desc" });
      return (data.data as unknown as Article[]);
    },
  });

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-5 py-20 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-destructive/50" />
        <h2 className="mt-4 font-heading text-xl font-semibold text-foreground">Failed to load articles</h2>
        <p className="mt-2 text-sm text-muted-foreground">Please try again later.</p>
      </div>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
      <div className="text-center mb-12">
        <div className="eyebrow flex items-center justify-center gap-2 mb-4">
          <BookOpen className="h-3.5 w-3.5 text-primary" /> Articles
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-foreground">
          Expert Articles
        </h1>
        <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl mx-auto">
          Insights from HR leaders and industry experts on the trends shaping the future of work.
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          [1, 2, 3, 4].map((i) => <SkeletonArticle key={i} />)
        ) : !articles || articles.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground/30" />
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground">No articles yet</h3>
            <p className="mt-2 text-sm text-muted-foreground">Check back soon for new articles.</p>
          </div>
        ) : (
          articles.map((article: Article) => (
            <Card key={article.id} className="group hover:border-primary/30 transition-all duration-200">
              <CardContent className="p-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                  <FileText className="h-5 w-5" />
                </div>
                <Badge variant="secondary" className="mb-3 text-[0.625rem]">{article.category}</Badge>
                <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{article.author}</span>
                  <span className="text-neutral-300">·</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{article.readingTime ?? "8 min read"}</span>
                </div>
                <Button variant="ghost" size="sm" asChild className="mt-4 p-0 h-auto text-xs gap-1">
                  <Link href={`/articles/${article.id}`}>
                    Read more <ArrowRight className="h-3 w-3" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </main>
  );
}
